import type { Hex } from '@/store/ids';

/**
 * The typed request/response calls the page can ask the worker to make.
 *
 * Phase 1 added one bespoke message pair per query (`resolve-user`,
 * `resolve-user-result`). Phases 2–4 add roughly fifteen more, and fifteen
 * hand-written pairs would be fifteen chances to forget a case in a switch. So
 * there is one pair — `call` and `call-result` — and the *names* live in a map
 * whose keys index both the request and the response type. The worker's
 * dispatch table is typed as a mapped type over the same map, so a call added
 * here is a compile error until the worker implements it.
 *
 * Everything in this file must be structured-clonable: plain objects, arrays,
 * strings, numbers, booleans, `Uint8Array` and `Blob`. No class instances, no
 * functions, no protobuf messages.
 *
 * Most list calls return only paging information. The rows themselves are
 * written to LocalStore by the worker, because Section 14 requires the
 * interface to observe LocalStore rather than hold a second authoritative copy
 * fed from the socket. A call that returns rows directly does so only where
 * there is no LocalStore table to hold them — search hits and join requests are
 * transient query results, not durable state.
 */

/** Paging information shared by every list call. */
export interface PageResult {
  nextCursor: Uint8Array | null;
  hasMore: boolean;
}

export interface SearchHitResult {
  conversationId: Hex;
  conversationTitle: string;
  messageId: Hex;
  senderId: Hex;
  createdSeq: number;
  createdAt: number;
  text: string;
  /** Ranges the server matched, in Unicode scalar offsets. */
  highlights: { offset: number; length: number }[];
}

export interface ProfileResult {
  userId: Hex;
  displayName: string;
  username: string | null;
}

export interface PublicHitResult {
  kind: string;
  id: Hex;
  title: string;
  username: string | null;
}

export interface DownloadGrantResult {
  url: string;
  expiresAt: number;
  etag: string;
}

export interface WorkerCallMap {
  'search-history': {
    request: {
      conversationIds: Hex[];
      query: string;
      senderId: Hex | null;
      mediaKind: string | null;
      from: number | null;
      to: number | null;
      cursor: Uint8Array | null;
      limit: number;
    };
    response: PageResult & { hits: SearchHitResult[] };
  };
  'search-public': {
    request: { query: string; kind: number; limit: number };
    response: PageResult & { results: PublicHitResult[] };
  };
  'list-members': {
    request: { conversationId: Hex; cursor: Uint8Array | null; limit: number };
    response: PageResult;
  };
  'list-topics': {
    request: { conversationId: Hex; cursor: Uint8Array | null; limit: number };
    response: PageResult;
  };
  'list-pins': {
    request: { conversationId: Hex; cursor: Uint8Array | null; limit: number };
    response: PageResult;
  };
  'list-reactions': {
    request: { conversationId: Hex; messageId: Hex; cursor: Uint8Array | null; limit: number };
    response: PageResult;
  };
  'list-join-requests': {
    request: { conversationId: Hex; cursor: Uint8Array | null; limit: number };
    response: PageResult & { users: ProfileResult[] };
  };
  'get-poll': {
    request: { conversationId: Hex; pollId: Hex };
    response: { found: boolean };
  };
  'get-media-status': {
    request: { mediaId: Hex };
    response: { state: string };
  };
  'get-download-grant': {
    request: {
      conversationId: Hex | null;
      messageId: Hex | null;
      mediaId: Hex;
      variant: string;
      /**
       * What the object is being fetched as.
       *
       * Omitted means content, which preserves the existing attachment and story
       * behaviour exactly. A conversation photo is authorized through the
       * conversation rather than a message, so it carries no message id.
       */
      purpose?: 'content' | 'conversation-photo';
    };
    response: DownloadGrantResult;
  };
  'record-view': {
    request: { conversationId: Hex; messageId: Hex };
    response: { accepted: boolean };
  };
  /**
   * Presence for a handful of accounts.
   *
   * A pull, asked when a conversation opens and refreshed while it stays open.
   * The server returns one entry per account asked about whether or not it is
   * visible, so a missing entry never carries information.
   */
  'get-presence': {
    request: { userIds: Hex[] };
    response: {
      entries: { userId: Hex; online: boolean; lastSeenAt: number | null }[];
    };
  };
  /** Begins or resumes a transfer whose row already exists in LocalStore. */
  'upload-start': {
    request: { uploadId: Hex };
    response: { started: boolean };
  };
  'upload-pause': {
    request: { uploadId: Hex };
    response: { paused: boolean };
  };
  'upload-cancel': {
    request: { uploadId: Hex };
    response: { cancelled: boolean };
  };
}

export type WorkerCallName = keyof WorkerCallMap;
export type WorkerCallRequest<K extends WorkerCallName> = WorkerCallMap[K]['request'];
export type WorkerCallResponse<K extends WorkerCallName> = WorkerCallMap[K]['response'];

/** The worker's dispatch table. One entry per call, checked by the compiler. */
export type WorkerCallHandlers = {
  [K in WorkerCallName]: (request: WorkerCallRequest<K>) => Promise<WorkerCallResponse<K>>;
};

/**
 * A failed call.
 *
 * The code is the server's error code where there was one, `OFFLINE` when this
 * context has no authenticated socket, and `TRANSPORT` otherwise. Callers
 * distinguish them because Section 14 requires a search outage
 * (`SEARCH_UNAVAILABLE`) to be presented differently from being offline.
 */
export class WorkerCallError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'WorkerCallError';
    this.code = code;
  }
}
